/**
 * CapabilityPage.tsx — the whole Engine surface, listed.
 *
 * The Engine publishes 179 capabilities in 19 presentation groups on the
 * qualified head: drawing, process, economics, electrical, safety, scenario,
 * production, topology, lifecycle, projections and the rest. This page lists
 * them BY THE ENGINE'S OWN GROUPS and runs the generic loop over whichever one
 * is selected.
 *
 * Why that is the multidisciplinary workbench rather than a stop on the way to
 * one: P&ID editing, electrical editing, panel editing, ATEX editing and safety
 * editing are the same interaction with different descriptors. When the Engine
 * publishes a capability for one of those areas, it appears in its own
 * Engine-declared group and becomes usable here, with no Web edit. A client
 * that shipped a screen per discipline would have to be taught about each new
 * capability, which is the coupling this page removes.
 *
 * WHAT IS DELIBERATELY ABSENT
 * ---------------------------
 * There is no excluded-vocabulary surface here, and no list of forbidden
 * identifiers that keeps one out. Automation is not admitted in this scope, and
 * the absence holds because the authoritative Engine manifest advertises no
 * capability in that area: a capability the Engine does not publish cannot be
 * listed here, so absence is the guarantee.
 *
 * A list would invert that. It would be a second, driftable source of truth
 * about a manifest this repository does not own, and the one artefact that could
 * make a manifest change look like nothing had changed — a list missing an entry
 * is indistinguishable from a list that is correct. So the guarantee is absence
 * in the manifest, and nothing here is positioned to override it.
 */

import { useMemo, useState } from "react";
import { useClient } from "../../api/ClientContext";
import { CapabilityManifestError } from "../../api/capabilityManifest";
import { groupedByPresentation, requiresHumanReview, type CapabilityManifest } from "../../api/capabilityManifest";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { CapabilityWorkbench } from "./CapabilityWorkbench";
import { ManifestRefusal } from "./ManifestRefusal";

export function CapabilityPage({ projectId }: { projectId: string }): JSX.Element {
  const client = useClient();
  // The manifest is the ONLY source of what the Engine supports, and it is
  // fetched fresh. Nothing in this repository can add a capability to it, and
  // nothing can remove one.
  const published = useAsync(() => client.capabilities(), [client]);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [filter, setFilter] = useState("");

  const groups = useMemo(() => {
    if (!published.data) return [];
    return [...groupedByPresentation(published.data).entries()];
  }, [published.data]);

  const needle = filter.trim().toLowerCase();

  return (
    <div className="page">
      <header className="page-head">
        <h1>Engine capabilities</h1>
        <p className="muted">
          {published.data
            ? `${published.data.capabilities.length} capabilities · ${published.data.projections.length} projections · contract ${published.data.contract}`
            : "loading the Engine's published manifest…"}
        </p>
      </header>

      {/* A manifest refusal is TERMINAL, so this is not offered a retry. */}
      {published.error instanceof CapabilityManifestError && <ManifestRefusal error={published.error} />}
      <ErrorBanner error={published.error && !(published.error instanceof CapabilityManifestError) ? published.error : undefined} onDismiss={published.reload} />

      {published.data && <ManifestSummary manifest={published.data} />}

      <div className="capability-layout">
        <aside className="capability-catalogue">
          <Panel title="Catalogue" actions={
            <input
              type="search" value={filter} placeholder="Filter" aria-label="Filter capabilities"
              onChange={event => setFilter(event.target.value)}
            />
          }>
            {groups.length === 0 ? <Empty>No groups published.</Empty> : groups.map(([group, descriptors]) => {
              const visible = needle
                ? descriptors.filter(d => `${d.id} ${d.presentation.summary}`.toLowerCase().includes(needle))
                : descriptors;
              if (visible.length === 0) return null;
              return (
                <section key={group}>
                  <h3>{group}</h3>
                  <ul className="capability-list">
                    {visible.map(descriptor => (
                      <li key={descriptor.id}>
                        <button
                          type="button"
                          className={descriptor.id === selected ? "is-armed" : undefined}
                          onClick={() => setSelected(descriptor.id)}
                        >
                          <span className="capability-id">{descriptor.id}</span>
                          <span className="muted">{descriptor.mode} · {descriptor.review}</span>
                          {requiresHumanReview(descriptor) && <span className="muted">independent human required</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </Panel>
        </aside>

        <main className="capability-main">
          {selected ? (
            <CapabilityWorkbench projectId={projectId} manifest={published.data!} capabilityId={selected} />
          ) : (
            <Panel title="Select a capability">
              <Empty>
                Choose a capability from the Engine&apos;s own catalogue. Its parameters, permitted values, review
                policy and result shape all come from the descriptor the Engine published.
              </Empty>
            </Panel>
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * What this browser qualified against.
 *
 * The digest is here so a reviewer can tell at a glance whether the Engine
 * underneath the workbench is the one the build was tested against. It is
 * displayed, never enforced at runtime: pinning is a deployment decision and
 * `fetchCapabilityManifest` takes an expected digest for deployments that make
 * one.
 */
function ManifestSummary({ manifest }: { manifest: CapabilityManifest }): JSX.Element {
  const generic = manifest.transports.filter(transport => transport.generic);
  return (
    <Panel title="This Engine" tone="default">
      <dl className="facts compact">
        <dt>Contract</dt><dd><code>{manifest.contract}</code></dd>
        <dt>Supported majors</dt><dd>{manifest.supportedMajors.join(", ")}</dd>
        <dt>Unsupported version</dt><dd>{manifest.unsupportedVersionBehaviour}</dd>
        <dt>Digest</dt><dd className="request-id">{manifest.digest || "—"}</dd>
        <dt>Generated from</dt><dd><code>{manifest.generatedFrom || "—"}</code></dd>
        <dt>Generic transports</dt><dd>{generic.map(t => <code key={t.id}>{t.id}</code>).reduce<React.ReactNode[]>((acc, node, i) => i === 0 ? [node] : [...acc, ", ", node], [])}</dd>
      </dl>
    </Panel>
  );
}
